<script setup lang="ts">
import { Delete } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { reactive, watch } from 'vue'

// ==================== Props & Emits ====================
interface Props {
  node: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  update: [properties: any]
}>()

// ==================== 状态定义 ====================
const formData = reactive<any>({
  text: '',
  properties: {
    description: '',
    approverType: 'user',
    approverMode: 'sequential',
    approvers: [],
    conditions: [],
    ccUsers: [],
    timeout: 24,
    autoPass: false,
  },
})

// ==================== 方法定义 ====================

// 监听节点变化
watch(() => props.node, (newNode) => {
  if (newNode) {
    formData.text = newNode.text || ''
    formData.properties = {
      ...formData.properties,
      ...(newNode.properties || {}),
    }
  }
}, { immediate: true, deep: true })

// 保存配置
function handleSave() {
  emit('update', formData.properties)
  ElMessage.success('配置已保存')
}

// 选择审批人（TODO: 实现用户选择对话框）
function handleSelectApprovers() {
  ElMessage.info('审批人选择功能开发中...')
}

// 移除审批人
function handleRemoveApprover(index: number) {
  formData.properties.approvers.splice(index, 1)
}

// 添加条件
function handleAddCondition() {
  formData.properties.conditions.push({
    field: '',
    operator: 'eq',
    value: '',
  })
}

// 移除条件
function handleRemoveCondition(index: number) {
  formData.properties.conditions.splice(index, 1)
}

// 选择抄送人
function handleSelectCcUsers() {
  ElMessage.info('抄送人选择功能开发中...')
}

// 移除抄送人
function handleRemoveCcUser(index: number) {
  formData.properties.ccUsers.splice(index, 1)
}
</script>

<template>
  <div class="node-property-panel">
    <el-form :model="formData" label-width="80px" label-position="top">
      <!-- 基础信息 -->
      <el-form-item label="节点名称">
        <el-input v-model="formData.text" placeholder="请输入节点名称" />
      </el-form-item>

      <el-form-item label="节点描述">
        <el-input
          v-model="formData.properties.description"
          type="textarea"
          :rows="3"
          placeholder="请输入节点描述"
        />
      </el-form-item>

      <!-- 审批节点配置 -->
      <template v-if="node.type === 'approval'">
        <el-divider content-position="left">
          审批配置
        </el-divider>

        <el-form-item label="审批人类型">
          <el-select v-model="formData.properties.approverType" placeholder="请选择">
            <el-option label="指定用户" value="user" />
            <el-option label="指定角色" value="role" />
            <el-option label="指定部门" value="department" />
            <el-option label="自定义" value="custom" />
          </el-select>
        </el-form-item>

        <el-form-item label="审批方式">
          <el-select v-model="formData.properties.approverMode" placeholder="请选择">
            <el-option label="依次审批" value="sequential" />
            <el-option label="会签（需所有人通过）" value="parallel" />
            <el-option label="或签（任一人通过即可）" value="any" />
          </el-select>
        </el-form-item>

        <el-form-item label="审批人">
          <el-button size="small" @click="handleSelectApprovers">
            选择审批人
          </el-button>
          <div v-if="formData.properties.approvers?.length" class="approver-list">
            <el-tag
              v-for="(approver, index) in formData.properties.approvers"
              :key="index"
              closable
              class="mr-2 mt-2"
              @close="handleRemoveApprover(index)"
            >
              {{ approver.name }}
            </el-tag>
          </div>
        </el-form-item>

        <el-form-item label="超时时间">
          <el-input-number
            v-model="formData.properties.timeout"
            :min="1"
            :max="720"
            placeholder="小时"
          />
          <span class="ml-2 text-sm text-gray-500">小时</span>
        </el-form-item>

        <el-form-item label="超时处理">
          <el-checkbox v-model="formData.properties.autoPass">
            超时自动通过
          </el-checkbox>
        </el-form-item>
      </template>

      <!-- 条件节点配置 -->
      <template v-if="node.type === 'condition'">
        <el-divider content-position="left">
          条件配置
        </el-divider>

        <el-form-item label="条件规则">
          <el-button size="small" @click="handleAddCondition">
            添加条件
          </el-button>
          <div v-if="formData.properties.conditions?.length" class="condition-list">
            <div
              v-for="(condition, index) in formData.properties.conditions"
              :key="index"
              class="condition-item"
            >
              <el-input v-model="condition.field" placeholder="字段" size="small" />
              <el-select v-model="condition.operator" placeholder="操作符" size="small">
                <el-option label="等于" value="eq" />
                <el-option label="不等于" value="ne" />
                <el-option label="大于" value="gt" />
                <el-option label="小于" value="lt" />
                <el-option label="包含" value="contains" />
              </el-select>
              <el-input v-model="condition.value" placeholder="值" size="small" />
              <el-button
                type="danger"
                size="small"
                :icon="Delete"
                @click="handleRemoveCondition(index)"
              />
            </div>
          </div>
        </el-form-item>
      </template>

      <!-- 抄送节点配置 -->
      <template v-if="node.type === 'cc'">
        <el-divider content-position="left">
          抄送配置
        </el-divider>

        <el-form-item label="抄送人">
          <el-button size="small" @click="handleSelectCcUsers">
            选择抄送人
          </el-button>
          <div v-if="formData.properties.ccUsers?.length" class="cc-user-list">
            <el-tag
              v-for="(user, index) in formData.properties.ccUsers"
              :key="index"
              closable
              class="mr-2 mt-2"
              @close="handleRemoveCcUser(index)"
            >
              {{ user.name }}
            </el-tag>
          </div>
        </el-form-item>
      </template>

      <!-- 保存按钮 -->
      <el-form-item>
        <el-button type="primary" @click="handleSave">
          保存配置
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<style scoped lang="scss">
.node-property-panel {
  .approver-list,
  .cc-user-list {
    margin-top: 8px;
  }

  .condition-list {
    margin-top: 8px;

    .condition-item {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
    }
  }
}
</style>
